<template>
  <div class="relative">
    <Input
      v-model="searchInput"
      :class="{ 'border-red-400': !isInputValid && searchInput }"
      :loading="isLoading"
      placeholder="Filter by role"
      icon="i-heroicons-magnifying-glass"
      @focus="handleFocus"
      @input="onSearch"
    />
    <div
      v-if="isClient && showSuggestions && suggestedRoles.length > 0"
      class="suggestions-box"
    >
      <button
        v-for="role in suggestedRoles"
        :key="role.soc_code"
        class="suggestion-item"
        @click.stop="selectRole(role)"
      >
        {{ role.popular_title || role.title }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'

function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

export interface Role {
  title: string
  popular_title?: string
  soc_code: string
  soc_code_id: string
  count: number
}
const props = defineProps<{
  permalink: string
  initialRole?: string
  mode?: 'salaries' | 'reviews'
}>()
const emit = defineEmits<{
  'update:selected': [Role | null]
  'update:input': [string]
  'update:isValid': [boolean]
  'update:exactMatch': [boolean]
}>()

const suggestedRoles = ref<Role[]>([])
const selectedRole = ref<Role | null>(null)
const searchInput = ref(props.initialRole || '')
const showSuggestions = ref(false)
const isLoading = ref(false)
const isInputValid = ref(true)
const isClient = ref(false)

function normalizeText(text: string): string {
  if (!text) return ''
  return text
    .replace(/[^\w\s]|_/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

interface RolesApiResponse {
  success: boolean
  data?: Role[]
  error?: string
}

async function fetchRoles(search: string = '') {
  if (isLoading.value) return

  try {
    isLoading.value = true
    const params = new URLSearchParams()
    if (props.mode) params.set('mode', props.mode)
    if (search) params.set('search', search)
    params.set('limit', '10')

    const queryString = params.toString()
    const url = `/api/companies/${props.permalink}/roles${queryString ? `?${queryString}` : ''}`

    const response = await $fetch<RolesApiResponse>(url)
    if (response.success && response.data && Array.isArray(response.data)) {
      suggestedRoles.value = response.data
      updateValidation()
      return true
    }
    console.error(
      'Failed to fetch roles:',
      response.error || 'Unknown error'
    )
    return false
  } catch (error) {
    console.error('Error fetching roles:', error)
    return false
  } finally {
    isLoading.value = false
  }
}

const debouncedFetchRoles = debounce((search: string) => {
  fetchRoles(search)
}, 300)

function updateValidation() {
  if (!searchInput.value.trim()) {
    isInputValid.value = true
    emit('update:isValid', true)
    emit('update:exactMatch', false)
    return
  }

  const normalizedInput = normalizeText(searchInput.value)
  const hasExactMatch = suggestedRoles.value.some((role) => {
    const titleMatch = normalizeText(role.title) === normalizedInput
    const popularTitleMatch
      = role.popular_title
        && normalizeText(role.popular_title) === normalizedInput
    return (
      titleMatch || popularTitleMatch || role.soc_code === searchInput.value
    )
  })

  isInputValid.value = hasExactMatch
  emit('update:isValid', hasExactMatch)
  emit('update:exactMatch', hasExactMatch)
}

function findRoleMatch(roleName: string): Role | null {
  if (!roleName || !suggestedRoles.value.length) return null
  const normalizedInput = normalizeText(roleName)
  const exactMatch = suggestedRoles.value.find(
    role =>
      normalizeText(role.title) === normalizedInput
      || (role.popular_title
        && normalizeText(role.popular_title) === normalizedInput)
  )
  if (exactMatch) return exactMatch
  return (
    suggestedRoles.value.find(
      role =>
        normalizeText(role.title).includes(normalizedInput)
        || (role.popular_title
          && normalizeText(role.popular_title).includes(normalizedInput))
    ) || null
  )
}

async function initializeWithInitialRole() {
  if (props.initialRole && !selectedRole.value) {
    // Fetch roles matching the initial role to validate it
    await fetchRoles(props.initialRole)
    const matchedRole = findRoleMatch(props.initialRole)
    if (matchedRole) {
      selectedRole.value = matchedRole
      searchInput.value = matchedRole.popular_title || matchedRole.title
      emit('update:selected', matchedRole)
      emit('update:isValid', true)
      emit('update:exactMatch', true)
    }
  }
}

function selectRole(role: Role) {
  selectedRole.value = role
  searchInput.value = role.popular_title || role.title
  showSuggestions.value = false
  isInputValid.value = true
  emit('update:selected', role)
  emit('update:input', searchInput.value)
  emit('update:isValid', true)
  emit('update:exactMatch', true)
}

function handleFocus() {
  showSuggestions.value = true
  // Fetch initial suggestions if none loaded
  if (suggestedRoles.value.length === 0) {
    fetchRoles(searchInput.value)
  }
}

function onSearch(value: string | Event) {
  const inputValue
    = typeof value === 'string'
      ? value
      : (value.target as HTMLInputElement).value
  searchInput.value = inputValue
  showSuggestions.value = true
  emit('update:input', inputValue)

  // Clear selected role when user types
  if (selectedRole.value) {
    const currentRoleTitle = selectedRole.value.popular_title || selectedRole.value.title
    if (normalizeText(inputValue) !== normalizeText(currentRoleTitle)) {
      selectedRole.value = null
      emit('update:selected', null)
    }
  }

  // Debounced API call for suggestions
  debouncedFetchRoles(inputValue)

  // Immediate validation feedback
  if (!inputValue.trim()) {
    isInputValid.value = true
    emit('update:isValid', true)
    emit('update:exactMatch', false)
    selectedRole.value = null
    emit('update:selected', null)
  }
}

function handleOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.suggestions-box') && !target.closest('input')) {
    showSuggestions.value = false
  }
}

function findRoleBySocCodeId(socCodeId: string): Role | null {
  if (!socCodeId || !suggestedRoles.value.length) return null
  return (
    suggestedRoles.value.find(role => role.soc_code_id === socCodeId) || null
  )
}

onMounted(() => {
  isClient.value = true
  if (props.initialRole) {
    initializeWithInitialRole()
  }
  if (import.meta.client) {
    document.addEventListener('click', handleOutsideClick)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('click', handleOutsideClick)
  }
})

watch(
  () => props.permalink,
  (newPermalink, oldPermalink) => {
    if (newPermalink && oldPermalink && newPermalink !== oldPermalink) {
      suggestedRoles.value = []
      selectedRole.value = null
      searchInput.value = ''
    }
  }
)

defineExpose({
  fetchRoles,
  findRoleBySocCodeId,
  findRoleMatch,
  selectRole,
  searchInput,
  selectedRole,
  isInputValid
})
</script>

<style scoped>
.suggestions-box {
  @apply absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto;
}

.suggestion-item {
  @apply w-full px-4 py-2 text-left hover:bg-gray-100 flex justify-between items-center;
}
</style>
